import { AfterViewInit, Component, effect, inject, input, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { NavigationExtras, Router } from "@angular/router";
import { forkJoin } from "rxjs";
import { take } from "rxjs/operators";
import { LatLngBounds, Marker } from "leaflet";
import { MatDialog } from "@angular/material/dialog";
import { LorenAsset } from "../../../loren-assets/interfaces/loren-asset.interface";

import {
  LorenNavService
} from "../../../../shared/modules/loren-nav/components/loren-nav/services/loren-nav-service/loren-nav.service";
import {
  LorenUserLocationService
} from "../../../../shared/services/loren-user-location-service/loren-user-location.service";
import { LorenAssetService } from "../../../loren-assets/services/loren-asset-service/loren-asset.service";
import {
  LorenBreakpointObserverService
} from "../../../../shared/services/loren-breakpoint-observer-service/loren-breakpoint-observer.service";
import { LorenLayerService } from "../../../../shared/services/loren-layer-service/loren-layer.service";
import {
  LorenLayerUtilsService
} from "../../../../shared/services/loren-layer-service/services/loren-layer-utils-service/loren-layer-utils.service";
import {
  LorenFeatureToggleService
} from "../../../../shared/services/loren-feature-toggle-service/loren-feature-toggle.service";
import {
  LorenDetailsOutletService
} from "../../../../shared/services/loren-details-outlet-service/loren-details-outlet.service";
import {
  LorenAssetRefresherService
} from "../../../../shared/services/loren-asset-refresher-service/loren-asset-refresher.service";
import { LorenBaseMapFeature } from "../../../../shared/base-components/loren-base-map-feature/loren-base-map-feature";
import {
  LorenAuthenticationService
} from "../../../../shared/services/loren-authentication-service/loren-authentication.service";
import { getAssetLocation } from "../../../asset/asset.utils";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {getMapDefaults} from "../../../asset-map/map.defaults";

@Component({
  selector: "loren-map",
  templateUrl: "./loren-map.component.html",
  styleUrls: ["./loren-map.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LorenMapComponent
  extends LorenBaseMapFeature
  implements OnInit, OnDestroy, AfterViewInit {
  dialog = inject(MatDialog);
  protected lorenNavService: LorenNavService;
  protected locationService: LorenUserLocationService;
  protected layerService: LorenLayerService;
  protected layerUtilsService: LorenLayerUtilsService;
  protected toggleService: LorenFeatureToggleService;
  protected breakpointObserverService: LorenBreakpointObserverService;
  protected router = inject(Router);
  protected lorenAssetService = inject(LorenAssetService);
  protected detailsOutletService = inject(LorenDetailsOutletService);
  protected assetRefresherService = inject(LorenAssetRefresherService);
  private authService = inject(LorenAuthenticationService);

  static title = "features.map";
  options = getMapDefaults();


  protected showPolylines = true;
  assets = input<LorenAsset[]>([]);
  lastSelectedAsset: LorenAsset;

  private assetMarkerMap: Map<number, Marker> = new Map<number, Marker>();
  private readonly extras: NavigationExtras;
  protected userId: string;

  constructor() {
    const lorenNavService = inject(LorenNavService);
    const locationService = inject(LorenUserLocationService);
    const layerService = inject(LorenLayerService);
    const layerUtilsService = inject(LorenLayerUtilsService);
    const toggleService = inject(LorenFeatureToggleService);
    const breakpointObserverService = inject(LorenBreakpointObserverService);

    super();
    this.lorenNavService = lorenNavService;
    this.locationService = locationService;
    this.layerService = layerService;
    this.layerUtilsService = layerUtilsService;
    this.toggleService = toggleService;
    this.breakpointObserverService = breakpointObserverService;

    this.extras = this.router.currentNavigation()?.extras || { state: {} };

    effect(() => {
      this.assets().forEach((a) =>
        this.deleteMapLayer(this.assetMarkerMap.get(a.id))
      );
      this.assetMarkerMap.clear();

      if (this.markerClusterGroup) {
        this.markerClusterGroup.clearLayers();
      }

      this.onMapLoaded();
    });

  }

  ngOnInit() {
    this.userId = this.authService.getUserInfo().id;
    this.setUpMarkerClusterGroup();
    this.subscribeToMarkerSelect();

    if (this.extras.state) {
      this.showAssetFromNavExtras();
    }
  }

  ngOnDestroy(): void {
    this.assetMarkerMap.clear();
    this.detailsOutletService.toggleState(false);
  }

  ngAfterViewInit(): void {
    if (this.lastSelectedAsset) {
      this.navigateToMapMarker(this.lastSelectedAsset);
    }
    setTimeout(() => this.handleLayerButtonBottomOffset(), 50);
  }

  closeSidebar() {
    this.lastSelectedAsset = null;

    setTimeout(() => {
      if (this.mapInstance) {
        this.mapInstance.invalidateSize();
      }
    }, 100);
  }

  protected onMapLoaded(): void {
    if(!this.assets()) {
      return;
    }

    const subscriptions = this.assets().map((x) => {
      return this.lorenAssetService.getAssetImageObservable(x.image);
    });

    forkJoin(subscriptions)
      .pipe(take(1))
      .subscribe((res) => {
        this.assets().forEach((_, index) => {
          this.assets()[index].avatar = res[index];
        });

        this.prepareAssetsMarkers();
      });
  }

  private subscribeToMarkerSelect() {
    this.assetRefresherService.selectAsset$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((assetId) => {
        const asset = this.assets().find((x) => x.id === assetId);
        if (asset) {
          this.navigateToMapMarker(asset);
        }

        if (this.isHandset) {
          this.detailsOutletService.resetDetailsOutlet();
          this.detailsOutletService.toggleState(false);
        }
        if (assetId) {
          this.assetRefresherService.selectAsset(null);
        }
      })

  }


  private showAssetFromNavExtras() {
    const id = this.extras.state?.assetId;
    if (id) {
      this.lastSelectedAsset = this.assets().find((x) => x.id === id);

      setTimeout(() => {
        if (this.mapInstance) {
          this.mapInstance.invalidateSize();
        }
      }, 100);
    }
  }

  private navigateToMapMarker(asset: LorenAsset) {
    if (!asset || !this.mapInstance) {
      return;
    }

    this.resetLastSelectedAssetMarkerState();
    this.lastSelectedAsset = asset;

    setTimeout(() => {
      if (this.mapInstance) {
        this.mapInstance.invalidateSize();
      }
    }, 100);

    const assetMarker = this.assetMarkerMap.get(asset.id);
    if (!assetMarker) {
      return;
    }

    const element = assetMarker.getElement();
    const pin = element?.querySelector?.('.marker-pin');

    if (pin) {
      pin.classList.add("active-marker-pin");
    }

    try {
      if (this.mapInstance) {
        this.mapInstance.setView(assetMarker.getLatLng(), 18);
      }
    } catch (e) {
      console.warn('Map center error:', e);
    }

    if (this.isHandset && assetMarker.togglePopup) {
      assetMarker.togglePopup();
    }
  }

  private resetLastSelectedAssetMarkerState() {
    if (this.lastSelectedAsset?.location) {
      const lastMarker = this.assetMarkerMap.get(this.lastSelectedAsset.id);
      if (!lastMarker) {
        return;
      }
      const dom = lastMarker
        .getElement()
        ?.getElementsByClassName("marker-pin")[0] as HTMLDivElement;
      if (!dom) {
        return;
      }
      dom.style.background = "";
      dom.classList.remove("active-marker-pin");
      dom.classList.add("primary");
      lastMarker.closePopup();
    }
  }

  prepareAssetsMarkers() {
    const markers = [];

    this.assets().forEach((asset) => {
      const location = getAssetLocation(asset);
      if (!location) {
        return;
      }

      let mapMarker: Marker;
      if (this.assetMarkerMap.has(asset.id)) {
        mapMarker = this.assetMarkerMap.get(asset.id);
        mapMarker.setLatLng(location);
      } else {
        mapMarker = this.createNewMarker(location, asset);
        mapMarker.on("click", () => this.navigateToMapMarker(asset));
        if (this.markerClusterGroup) {
          mapMarker.addTo(this.markerClusterGroup);
        }
      }

      markers.push(mapMarker);
      this.assetMarkerMap.set(asset.id, mapMarker);
    });

    if (!this.hasInteracted && markers.length > 0 && !this.lastSelectedAsset) {
      this.centerAllMarkers(markers);
    } else if (this.lastSelectedAsset?.location && this.mapInstance) {
      this.mapInstance.fitBounds([
        [
          this.lastSelectedAsset.location.latitude,
          this.lastSelectedAsset.location.longitude,
        ],
      ]);
    }

    this.markerClusterData = markers;
    if (this.markerClusterGroup && this.mapInstance) {
      this.markerClusterGroup.addTo(this.mapInstance);
    }
  }

  centerAllMarkers(markers) {
    if (!this.mapInstance) {
      return;
    }

    const bounds = new LatLngBounds([]);

    markers.forEach((m) => {
      bounds.extend([m?.getLatLng().lat, m?.getLatLng().lng]);
    });
    this.mapInstance.fitBounds(bounds);
  }
}
